import React, { useMemo, useState, useEffect, useRef } from "react";
import EventCard from "../../components/EventManagement/NewFeed";
import ImageLightbox from "../../components/EventManagement/Component/ImageLightBox";
import { useGetActiveEvent } from "../../data/event";
import { convertPosts, sortActiveFeed, sortByEventStartDate } from "../../components/EventManagement/utils";


const ITEMS_PER_PAGE = 10;


/* ---------------- Pagination Helper ---------------- */
const getPaginationRange = (
 currentPage: number,
 totalPages: number
): (number | string)[] => {
 const delta = 1;
 const range: number[] = [];
 const rangeWithDots: (number | string)[] = [];
 let lastPage: number | undefined;


 for (let i = 1; i <= totalPages; i++) {
   if (
     i === 1 ||
     i === totalPages ||
     (i >= currentPage - delta && i <= currentPage + delta)
   ) {
     range.push(i);
   }
 }


 for (const page of range) {
   if (lastPage) {
     if (page - lastPage === 2) {
       rangeWithDots.push(lastPage + 1);
     } else if (page - lastPage > 2) {
       rangeWithDots.push("...");
     }
   }
   rangeWithDots.push(page);
   lastPage = page;
 }


 return rangeWithDots;
};


/* ---------------- Component ---------------- */
const SeekerViewEvent = () => {
 const { data } = useGetActiveEvent();


 const [currentPage, setCurrentPage] = useState(1);


 const scrollRef = useRef(null);


 const [lightboxState, setLightboxState] = useState({
   isOpen: false,
   data: null,
   initialIndex: 0,
 });


 /* ---------------- Lightbox ---------------- */
 const handleOpenLightbox = (data: any, index: number) => {
   setLightboxState({
     isOpen: true,
     data,
     initialIndex: index,
   });
   document.body.style.overflow = "hidden";
 };


 const handleCloseLightbox = () => {
   setLightboxState((prev:any) => ({ ...prev, isOpen: false }));
   document.body.style.overflow = "auto";
 };


 /* ---------------- Separate Active & Past Events ---------------- */
 const { activeEvents, pastEvents } = useMemo(() => {
   const now = new Date();
   const active: any[] = [];
   const past: any[] = [];


   data?.forEach((event: any) => {
     // Posts are always active
     if (event?.post_type === "post") {
       active.push(event);
       return;
     }


     if (!event?.eventStartDate?.seconds) return;


     const eventDate = new Date(event.eventStartDate.seconds * 1000);


     if (eventDate >= now) {
       active.push(event);
     } else {
       past.push(event);
     }
   });


   return {
     activeEvents: convertPosts(sortActiveFeed(active)),
     pastEvents: convertPosts(sortByEventStartDate(past, "desc")),
   };
 }, [data]);


 /* ---------------- Reset page when data changes ---------------- */
 useEffect(() => {
   setCurrentPage(1);
 }, [activeEvents.length]);


 /* ---------------- Pagination ---------------- */
 const totalPages = Math.max(
   1,
   Math.ceil(activeEvents.length / ITEMS_PER_PAGE)
 );


 const paginatedActiveEvents = useMemo(() => {
   const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
   return activeEvents.slice(
     startIndex,
     startIndex + ITEMS_PER_PAGE
   );
 }, [activeEvents, currentPage]);


 const paginationRange = useMemo(
   () => getPaginationRange(currentPage, totalPages),
   [currentPage, totalPages]
 );


 /* ---------------- Scroll to top on page change ---------------- */
 useEffect(() => {
   scrollRef.current?.scrollTo({
     top: 0,
     behavior: "smooth",
   });
 }, [currentPage]);


 /* ---------------- Render ---------------- */
 return (
   <>
     <div className="pt-[160px] overflow-y-auto h-screen w-screen xs:px-[1%] sm:px[2%]  px-[27%]">
       <div
         ref={scrollRef}
         className=" "
       >
         {/* -------- Active Events -------- */}
         {paginatedActiveEvents.length === 0 && (
           <div className="bg-white mt-2 shadow-sm p-6 text-center border">
             No events & posts
           </div>
         )}


         {paginatedActiveEvents.map((event: any) => (
           <div key={event.id} className="mt-5 pr-2">
             <EventCard
               data={event}
               feedData={event}
               onOpenLightbox={handleOpenLightbox}
             />
           </div>
         ))}


         {/* -------- Past Events -------- */}
         {pastEvents.length > 0 && (
           <div className="mt-10">
             <h1 className="text-2xl font-semibold text-instant-teams-blue-Main">
               Past Events
             </h1>


             {pastEvents.map((event: any) => (
               <div key={event.id} className="mt-5 pr-2">
                 <EventCard
                   data={event}
                   pastEvent
                   onOpenLightbox={handleOpenLightbox}
                 />
               </div>
             ))}
           </div>
         )}


     
             </div>
            
                {/* -------- Pagination -------- */}
         {totalPages > 1 && (
           <div className="flex justify-center gap-2 mt-10 mb-10">
             {paginationRange.map((page:any, index:any) =>
               page === "..." ? (
                 <span key={index} className="px-3 py-1">
                   ...
                 </span>
               ) : (
                 <button
                   key={page}
                   onClick={() =>
                     typeof page === "number" &&
                     setCurrentPage(page)
                   }
                   className={`px-3 py-1 rounded border transition ${
                     currentPage === page
                       ? "bg-blue-600 text-white"
                       : "bg-white hover:bg-gray-100"
                   }`}
                 >
                   {page}
                 </button>
               )
             )}
           </div>
         )}
     </div>


     {/* -------- Image Lightbox -------- */}
     <ImageLightbox
       isOpen={lightboxState.isOpen}
       onClose={handleCloseLightbox}
       data={lightboxState.data}
       initialIndex={lightboxState.initialIndex}
     />
   </>
 );
};


export default SeekerViewEvent;
